/**
 * Static master list of all supported RV types.
 * This list is NOT generated dynamically — it covers every type
 * the dealership may ever stock, including types with zero current inventory.
 * The frontend filters this against the live JSON index.
 */
export const MASTER_RV_TYPES = [
  "Travel Trailer",
  "Lite Travel Trailer",
  "Fifth Wheel",
  "Motorhome",
  "Motor Home Class A",
  "Motor Home Class A - Diesel",
  "Motor Home Class B",
  "Motor Home Class B - Diesel",
  "Motor Home Class C",
  "Motor Home Class C - Diesel",
  "Toy Hauler Fifth Wheel",
  "Toy Hauler Travel Trailer",
  "Folding Pop-Up Camper",
  "Destination Trailer",
  "Expandable",
  "Other",
] as const;

export type RvTypeName = (typeof MASTER_RV_TYPES)[number];

/**
 * Expands a base type into itself + fuel/chassis variants so filtering by
 * "Motor Home Class A" also returns "Motor Home Class A - Diesel".
 * Returns [label] when the label already is a variant or has none.
 */
export function expandRvTypeFamily(label: string): string[] {
  const base = label.trim();
  if (base.includes(" - ")) return [base];
  const variants = MASTER_RV_TYPES.filter((t) => t.startsWith(`${base} - `));
  return [base, ...variants];
}

/** True when the label is a fuel/chassis variant (e.g. "… - Diesel"). */
export function isRvTypeVariant(label: string): boolean {
  return label.includes(" - ");
}

/**
 * Collapses variant labels into their base type for pickers: a base type is
 * offered whenever the base OR any of its variants has inventory, so choosing
 * "Motor Home Class A" returns gas + diesel units.
 */
export function collapseRvTypeFamilies(counts: Record<string, number>): string[] {
  return MASTER_RV_TYPES.filter((t) => {
    if (isRvTypeVariant(t)) return false;
    return expandRvTypeFamily(t).some((v) => (counts[v] ?? 0) > 0);
  });
}
